import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxEditorModule, Editor } from 'ngx-editor';
import { NavbarComponent } from '../navbar/navbar.component';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';

@Component({
  selector: 'app-admin-questions',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, NavbarComponent, NgxEditorModule],
  templateUrl: './admin-questions.component.html',
  styleUrl: './admin-users.component.css'
})
export class AdminQuestionsComponent implements OnInit, OnDestroy {
  @Input() embedded = false;
  sessionId = '';
  content: any[] = [];
  courses: { id: number; name: string }[] = [];
  subjects: { id: number; name: string; courseId: number }[] = [];
  topics: { id: number; name: string; subjectId: number }[] = [];
  areas: any[] = [];
  seeds: { id: number; name: string }[] = [];
  selectedSeedId: number | null = null;
  filterCourseId: number | null = null;
  filterSubjectId: number | null = null;
  filterTopicId: number | null = null;
  filterAreaId: number | null = null;
  filterShowRemoved = false;
  loading = false;
  error = '';
  message = '';
  detailError = '';
  detailItem: any = null;
  detailForm: any = {};
  detailSaving = false;
  deleteItem: any = null;
  deleteError = '';
  softDeleteItem: any = null;
  dialogTopics: { id: number; name: string; subjectId: number }[] = [];
  dialogAreas: { id: number; name: string; topicId?: number }[] = [];
  questionEditor: Editor | null = null;
  explanationEditor: Editor | null = null;
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;
  sortColumn = 'id';
  sortDir: 'asc' | 'desc' = 'desc';

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private adminRest: AdminRestService
  ) {}

  /** Convert JSON array string "[1,2,5]" to comma-separated "1,2,5" for display. */
  private idsToCommaSeparated(json: string | null | undefined): string {
    if (!json || typeof json !== 'string') return '';
    const s = json.trim();
    if (!s.startsWith('[') || !s.endsWith(']')) return s;
    return s.slice(1, -1).trim();
  }

  /** Convert comma-separated "1,2,5" to JSON array string "[1,2,5]" for API. */
  private commaSeparatedToJson(val: string | null | undefined): string | null {
    if (!val || typeof val !== 'string') return null;
    const ids = val.split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n));
    return ids.length > 0 ? '[' + ids.join(',') + ']' : null;
  }

  async ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    await this.loadSeeds();
    const defaultSeed = await this.adminRest.getAdminSeedDefault(this.sessionId);
    if (defaultSeed?.id != null) {
      this.selectedSeedId = defaultSeed.id;
    } else if (this.seeds.length > 0) {
      this.selectedSeedId = this.seeds[0].id;
    }
    await this.loadCourses();
  }

  ngOnDestroy() {
    this.destroyEditors();
  }

  private destroyEditors() {
    if (this.questionEditor) {
      this.questionEditor.destroy();
      this.questionEditor = null;
    }
    if (this.explanationEditor) {
      this.explanationEditor.destroy();
      this.explanationEditor = null;
    }
  }

  private createEditors() {
    this.destroyEditors();
    this.questionEditor = new Editor();
    this.explanationEditor = new Editor();
  }

  async loadCourses() {
    if (!this.sessionId) return;
    const list = await this.adminRest.getAdminCoursesList(this.sessionId, this.selectedSeedId ?? undefined);
    this.courses = list ?? [];
    if (this.courses.length > 0 && this.filterCourseId == null) {
      this.filterCourseId = this.courses[0].id;
      await this.onCourseChange(this.filterCourseId);
    }
  }

  async onSeedChange() {
    this.filterCourseId = null;
    this.filterSubjectId = null;
    this.filterTopicId = null;
    this.filterAreaId = null;
    this.subjects = [];
    this.topics = [];
    this.areas = [];
    await this.loadCourses();
    if (this.courses.length > 0) {
      this.filterCourseId = this.courses[0].id;
      await this.onCourseChange(this.filterCourseId);
    } else {
      this.load();
    }
  }

  async onCourseChange(courseId: number | null) {
    this.filterCourseId = courseId ?? null;
    this.currentPage = 0;
    this.subjects = [];
    this.topics = [];
    this.areas = [];
    this.filterSubjectId = null;
    this.filterTopicId = null;
    this.filterAreaId = null;
    if (!courseId || !this.sessionId) return;
    const list = await this.adminRest.getAdminSubjects(this.sessionId, courseId, this.selectedSeedId ?? undefined);
    this.subjects = list ?? [];
    if (this.subjects.length > 0) {
      this.filterSubjectId = this.subjects[0].id;
      this.onSubjectChange(this.filterSubjectId);
    } else {
      this.load();
    }
  }

  async onSubjectChange(subjectIdValue: number | null) {
    this.filterSubjectId = subjectIdValue ?? null;
    this.currentPage = 0;
    this.topics = [];
    this.areas = [];
    this.filterTopicId = null;
    this.filterAreaId = null;
    if (subjectIdValue == null || !this.sessionId) {
      this.load();
      return;
    }
    const list = await this.adminRest.getAdminTopics(this.sessionId, subjectIdValue, this.selectedSeedId ?? undefined);
    this.topics = list ?? [];
    this.filterTopicId = null; // default All
    // Load all areas for this subject so the table can show topic/area names for every question
    if (this.sessionId && this.topics.length > 0) {
      const allAreas: { id: number; name: string; topicId?: number }[] = [];
      for (const t of this.topics) {
        const areasList = await this.adminRest.getAdminAreas(this.sessionId, t.id, this.selectedSeedId ?? undefined);
        for (const a of areasList ?? []) {
          allAreas.push({ ...a, topicId: t.id });
        }
      }
      this.areas = allAreas;
    }
    this.load();
  }

  async onTopicChange(topicId: number | null) {
    this.filterTopicId = topicId ?? null;
    this.currentPage = 0;
    this.filterAreaId = null;
    // Keep this.areas as all areas for the subject (set in onSubjectChange) so table display works
    this.load();
  }

  onFilterChange() {
    this.currentPage = 0;
    this.load();
  }

  onRemovedFilterChange() {
    this.currentPage = 0;
    this.load();
  }

  async load() {
    if (!this.sessionId) return;
    if (this.filterCourseId == null || this.filterSubjectId == null) {
      this.content = [];
      this.totalElements = 0;
      this.totalPages = 0;
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      const result = await this.adminRest.getAdminQuestions(this.sessionId, {
        courseId: this.filterCourseId ?? undefined,
        subjectId: this.filterSubjectId ?? undefined,
        topicId: this.filterTopicId ?? undefined,
        areaId: this.filterAreaId ?? undefined,
        seedId: this.selectedSeedId ?? undefined,
        includeRemoved: this.filterShowRemoved,
        sort: `${this.sortColumn},${this.sortDir}`,
        page: this.currentPage,
        size: this.pageSize
      });
      if (result) {
        this.content = result.content;
        this.totalElements = result.totalElements;
        this.totalPages = result.totalPages;
        this.currentPage = result.number;
      } else {
        this.content = [];
        this.totalElements = 0;
        this.totalPages = 0;
      }
    } catch (e: any) {
      this.error = e?.message || 'Failed to load questions';
      this.content = [];
      this.totalElements = 0;
      this.totalPages = 0;
    } finally {
      this.loading = false;
    }
  }

  goToPage(page: number) {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.load();
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = column === 'id' ? 'desc' : 'asc';
    }
    this.currentPage = 0;
    this.load();
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
  }

  get paginationStartItem(): number {
    if (this.totalElements === 0) return 0;
    return this.currentPage * this.pageSize + 1;
  }

  get paginationEndItem(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.totalElements);
  }

  async loadSeeds() {
    if (!this.sessionId) return;
    const list = await this.adminRest.getAdminSeeds(this.sessionId);
    this.seeds = list ?? [];
  }

  async loadAreasForSubject() {
    if (!this.sessionId || !this.filterSubjectId) return;
    const topics = await this.adminRest.getAdminTopics(this.sessionId, this.filterSubjectId, this.selectedSeedId ?? undefined);
    this.areas = [];
    for (const t of (topics ?? [])) {
      const list = await this.adminRest.getAdminAreas(this.sessionId, t.id, this.selectedSeedId ?? undefined);
      for (const a of (list ?? [])) {
        this.areas.push({ ...a, topicId: t.id });
      }
    }
  }

  async openCreate() {
    this.detailError = '';
    this.createEditors();
    if (this.areas.length === 0 && this.filterSubjectId) {
      await this.loadAreasForSubject();
    }
    this.detailItem = { id: null };
    let subjectId = this.filterSubjectId ?? (this.subjects.length > 0 ? this.subjects[0].id : null);
    let topicId = this.filterTopicId ?? null;
    let areaId = this.filterAreaId ?? null;
    this.detailForm = {
      subjectId,
      topicId,
      areaId,
      questionHtml: '',
      answer1: '', answer2: '', answer3: '', answer4: '',
      correctAnswer: 'A',
      explanationHtml: '',
      questionImageIds: '', explanationQuestionImageIds: '',
      seedId: this.selectedSeedId,
      removed: 0
    };
    await this.loadDialogTopics(this.detailForm.subjectId);
    if (this.detailForm.topicId == null && this.dialogTopics.length > 0) {
      this.detailForm.topicId = this.dialogTopics[0].id;
    }
    await this.loadDialogAreas(this.detailForm.topicId);
    if (this.detailForm.areaId == null && this.dialogAreas.length > 0) {
      this.detailForm.areaId = this.dialogAreas[0].id;
    }
  }

  async openEdit(q: any) {
    this.detailError = '';
    this.createEditors();
    this.detailItem = q;
    if (this.filterSubjectId) {
      await this.loadAreasForSubject();
    }
    const area = this.areas.find((a: any) => a.id === q.areaId);
    const topicId = area?.topicId ?? this.filterTopicId ?? null;
    this.detailForm = {
      subjectId: this.filterSubjectId ?? null,
      topicId: topicId,
      areaId: q.areaId,
      questionHtml: q.questionHtml ?? '',
      answer1: q.answer1 ?? '', answer2: q.answer2 ?? '', answer3: q.answer3 ?? '', answer4: q.answer4 ?? '',
      correctAnswer: (q.correctAnswer === 'A' || q.correctAnswer === 'B' || q.correctAnswer === 'C' || q.correctAnswer === 'D') ? q.correctAnswer : 'A',
      explanationHtml: q.explanationHtml ?? '',
      questionImageIds: this.idsToCommaSeparated(q.questionImageIds), explanationQuestionImageIds: this.idsToCommaSeparated(q.explanationQuestionImageIds),
      seedId: q.seedId ?? null,
      removed: q.removed ?? 0
    };
    await this.loadDialogTopics(this.detailForm.subjectId);
    await this.loadDialogAreas(this.detailForm.topicId);
  }

  async loadDialogTopics(subjectId: number | null) {
    if (!subjectId || !this.sessionId) {
      this.dialogTopics = [];
      return;
    }
    const list = await this.adminRest.getAdminTopics(this.sessionId, subjectId, this.selectedSeedId ?? undefined);
    this.dialogTopics = list ?? [];
  }

  async loadDialogAreas(topicId: number | null) {
    if (!topicId || !this.sessionId) {
      this.dialogAreas = [];
      return;
    }
    const list = await this.adminRest.getAdminAreas(this.sessionId, topicId, this.selectedSeedId ?? undefined);
    this.dialogAreas = list ?? [];
  }

  async onDialogSubjectChange(subjectId: number | null) {
    this.detailForm.subjectId = subjectId ?? null;
    this.detailForm.topicId = null;
    this.detailForm.areaId = null;
    await this.loadDialogTopics(this.detailForm.subjectId);
    this.dialogAreas = [];
  }

  async onDialogTopicChange(topicId: number | null) {
    this.detailForm.topicId = topicId ?? null;
    this.detailForm.areaId = null;
    await this.loadDialogAreas(this.detailForm.topicId);
  }

  closeDetail() {
    this.detailError = '';
    this.destroyEditors();
    this.detailItem = null;
    this.load();
  }

  async save() {
    if (!this.sessionId) return;
    this.detailError = '';
    const errors: string[] = [];
    if (this.detailForm.topicId == null) errors.push('Topic is required');
    if (this.detailForm.areaId == null) errors.push('Area is required');
    if (!this.detailForm.questionHtml?.trim()) errors.push('Question is required');
    const a1 = (this.detailForm.answer1 ?? '').trim();
    const a2 = (this.detailForm.answer2 ?? '').trim();
    const a3 = (this.detailForm.answer3 ?? '').trim();
    const a4 = (this.detailForm.answer4 ?? '').trim();
    if (!a1) errors.push('Answer A is required');
    if (!a2) errors.push('Answer B is required');
    if (!a3) errors.push('Answer C is required');
    if (!a4) errors.push('Answer D is required');
    const correct = this.detailForm.correctAnswer;
    if (correct !== 'A' && correct !== 'B' && correct !== 'C' && correct !== 'D') {
      errors.push('Correct answer must be A, B, C, or D');
    }
    if (!this.detailForm.seedId) errors.push('Seed is required');
    if (errors.length > 0) {
      this.detailError = errors.join('. ');
      return;
    }
    this.detailSaving = true;
    try {
      if (this.detailItem?.id == null) {
        await this.adminRest.postAdminQuestion(this.sessionId, {
          areaId: this.detailForm.areaId,
          questionHtml: this.detailForm.questionHtml,
          answer1: a1,
          answer2: a2,
          answer3: a3,
          answer4: a4,
          correctAnswer: this.detailForm.correctAnswer || null,
          explanationHtml: this.detailForm.explanationHtml || null,
          questionImageIds: this.commaSeparatedToJson(this.detailForm.questionImageIds),
          explanationQuestionImageIds: this.commaSeparatedToJson(this.detailForm.explanationQuestionImageIds),
          seedId: this.detailForm.seedId
        });
        this.message = 'Question created.';
      } else {
        await this.adminRest.putAdminQuestion(this.sessionId, this.detailItem.id, {
          areaId: this.detailForm.areaId,
          questionHtml: this.detailForm.questionHtml,
          answer1: this.detailForm.answer1,
          answer2: this.detailForm.answer2,
          answer3: this.detailForm.answer3,
          answer4: this.detailForm.answer4,
          correctAnswer: this.detailForm.correctAnswer,
          explanationHtml: this.detailForm.explanationHtml,
          questionImageIds: this.commaSeparatedToJson(this.detailForm.questionImageIds),
          explanationQuestionImageIds: this.commaSeparatedToJson(this.detailForm.explanationQuestionImageIds),
          seedId: this.detailForm.seedId,
          removed: this.detailForm.removed
        });
        this.message = 'Question updated.';
      }
      this.closeDetail();
    } catch (e: any) {
      this.detailError = e?.message || 'Save failed';
    } finally {
      this.detailSaving = false;
    }
  }

  openDeleteConfirm(q: any) {
    this.deleteItem = q;
    this.deleteError = '';
  }

  closeDeleteConfirm() {
    this.deleteItem = null;
    this.deleteError = '';
  }

  async confirmHardDelete() {
    if (!this.deleteItem?.id || !this.sessionId) return;
    this.deleteError = '';
    try {
      await this.adminRest.deleteAdminQuestion(this.sessionId, this.deleteItem.id);
      this.closeDeleteConfirm();
      this.load();
    } catch (e: any) {
      this.deleteError = e?.error?.error || e?.message || 'Delete failed';
    }
  }

  openSoftDeleteConfirm(q: any) {
    this.softDeleteItem = q;
  }

  closeSoftDeleteConfirm() {
    this.softDeleteItem = null;
  }

  async confirmSoftDelete() {
    if (!this.softDeleteItem?.id || !this.sessionId) return;
    try {
      await this.adminRest.postAdminQuestionSoftDelete(this.sessionId, this.softDeleteItem.id);
      this.closeSoftDeleteConfirm();
      this.load();
    } catch (e: any) {
      this.error = e?.message || 'Soft delete failed';
    }
  }

  /** Areas for the Area filter dropdown: only areas of the selected topic, or empty when Topic is All */
  get areasForFilter(): { id: number; name: string; topicId?: number }[] {
    if (this.filterTopicId == null) return [];
    return this.areas.filter(a => a.topicId === this.filterTopicId);
  }

  areaName(id: number) {
    return this.areas.find(a => a.id === id)?.name ?? id;
  }

  topicNameForArea(areaId: number): string {
    const area = this.areas.find(a => a.id === areaId);
    if (!area?.topicId) return String(areaId);
    const topic = this.topics.find(t => t.id === area.topicId);
    return topic?.name ?? String(area.topicId);
  }

  stripHtml(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').slice(0, 80) + (html.length > 80 ? '…' : '');
  }
}
